import axios from 'axios';
import { setAuth, clearAuth } from '../utils/auth';

const getFrontendBaseUrl = () => process.env.REACT_APP_FRONTEND_URL || 'https://3pl.datavisual.ca';

// Create Axios instance with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
  timeout: 30000
  // Removed default Content-Type header to allow FormData uploads
});

// Check if token is valid JWT and not expired
export const checkTokenHealth = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;

    const parts = token.split('.');
    if (parts.length !== 3) return false;

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const payload = JSON.parse(jsonPayload);
    const currentTime = Math.floor(Date.now() / 1000);

    return !(payload.exp && payload.exp < currentTime);
  } catch (error) {
    return false;
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Helper to clear all auth storage and redirect to home (landing page)
const clearAuthAndRedirect = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.location.href = getFrontendBaseUrl();
};

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401: attempt token refresh (skip for login and refresh endpoints)
    if (error.response?.status === 401 && !originalRequest._retry &&
        !originalRequest.url.includes('/auth/refresh-token') &&
        !originalRequest.url.includes('/auth/login')) {
      originalRequest._retry = true;

      const currentToken = localStorage.getItem('token');

      if (currentToken) {
        try {
          const response = await axios.post(`${api.defaults.baseURL}/auth/refresh-token`, {}, {
            headers: { 'Authorization': `Bearer ${currentToken}` }
          });

          if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            if (response.data.user) {
              localStorage.setItem('user', JSON.stringify(response.data.user));
            }
            api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
            originalRequest.headers['Authorization'] = `Bearer ${response.data.token}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          clearAuthAndRedirect();
          return Promise.reject(new Error('Token refresh failed'));
        }
      }

      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Function to set auth token for axios requests
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
  }
};

// Authentication service
const _authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  verifyToken: (token) => api.get(`/auth/verify-token/${token}`),
  refreshToken: () => api.post('/auth/refresh-token'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  getProfile: () => api.get('/auth/me'),

  // Customer endpoints
  getCustomerProfile: () => api.get('/customers/profile'),

  logout: () => {
    setAuthToken(null);
  }
};

const _notificationsAPI = {
  getNotifications: async (params = {}) => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },
  markAsRead: async (id) => {
    const response = await api.put(`/notifications/${id}/read`);
    return response.data;
  },
  markAllAsRead: async () => {
    const response = await api.put('/notifications/read-all');
    return response.data;
  }
};

// Customer API calls
const _customerAPI = {
  getProfile: () => api.get('/customers/profile'),
  updateProfile: (data) => api.put('/customers/profile', data),
  getPackingSlipSettings: () => api.get('/customers/packing-slip-settings'),
  updatePackingSlipSettings: async (settingsData = {}) => {
    const hasFile = settingsData.logo_file instanceof File;
    if (hasFile || settingsData.remove_logo) {
      const formData = new FormData();
      formData.append('settings', JSON.stringify(settingsData.settings || {}));
      if (hasFile) {
        formData.append('logo_file', settingsData.logo_file);
      }
      if (settingsData.remove_logo) {
        formData.append('remove_logo', 'true');
      }
      const response = await api.put('/customers/packing-slip-settings', formData);
      return response.data;
    }

    const response = await api.put('/customers/packing-slip-settings', settingsData);
    return response.data;
  },
  getPackingSlipLogo: async () => {
    const response = await api.get('/customers/packing-slip-logo', { responseType: 'blob' });
    return response.data;
  },
  getPackingSlipLogoUrl: () => `${api.defaults.baseURL}/customers/packing-slip-logo`,
  getInventoryAssets: (itemId, params = {}) => safeApiCall(
    api.get(`/customers/inventory/${itemId}/assets`, { params }),
    { data: [], total: 0 }
  ),
  changePassword: (data) => api.post('/customers/change-password', data),
  
  // User management
  getUsers: () => safeApiCall(
    api.get('/customers/users'),
    { data: [] }
  ),
  inviteUser: (userData) => api.post('/customers/users/invite', userData),
  updateUser: (userId, userData) => api.put(`/customers/users/${userId}`, userData),
  deleteUser: (userId) => api.delete(`/customers/users/${userId}`),
  activateUser: (userId) => api.post(`/customers/users/${userId}/activate`),
  deactivateUser: (userId) => api.post(`/customers/users/${userId}/deactivate`),

  // Contracts (read-only, customer admin only)
  getContracts: () => safeApiCall(
    api.get('/customers/contracts'),
    { data: [] }
  ),

  // Current user's personal settings (name/email/role/last login/per-user
  // notification preference overrides).
  getMySettings: () => api.get('/customers/me'),
  updateMyNotificationPreferences: (notificationPreferences) =>
    api.put('/customers/me/notification-preferences', { notificationPreferences }),
  submitFeedback: (payload) => api.post('/customers/feedback', payload),
  getHelpDocs: () => safeApiCall(
    api.get('/customers/help-docs'),
    { docs: [] }
  ),

  // Accessible internal (destination) warehouses for the current customer.
  getAccessibleWarehouses: () => safeApiCall(
    api.get('/customers/accessible-warehouses'),
    { data: [] }
  ),

  // Customer's own fee schedule (read-only, customer admin only).
  getMyFees: () => safeApiCall(
    api.get('/customers/my-fees'),
    { data: [] }
  ),

  // Handling-inbound + storage fee rates for ASN estimate (any customer user).
  getInboundEstimateFees: (warehouseId) => safeApiCall(
    api.get('/customers/inbound-estimate-fees', { params: { warehouse_id: warehouseId || undefined } }),
    { data: [], flatRateStorage: null }
  ),

  // Project autocomplete (shipping form)
  getProjects: (search) => safeApiCall(
    api.get('/customers/projects', { params: { search } }),
    { data: [] }
  ),

  // Activity timeline
  getActivity: (params = {}) => safeApiCall(
    api.get('/customers/activity', { params }),
    { data: [], total: 0, page: 1, limit: 25, totalPages: 0 }
  ),
  getEntityTimeline: (entityType, entityId, params = {}) => safeApiCall(
    api.get(`/customers/activity/${entityType}/${entityId}`, { params }),
    { data: [], total: 0, page: 1, limit: 50, totalPages: 0 }
  ),
};

// Wrapper for API calls that handles 403 errors with empty data
const safeApiCall = async (apiPromise, emptyData) => {
  try {
    const response = await apiPromise;
    return response.data;
  } catch (error) {
    if (error.response?.status === 403) {
      return emptyData;
    }
    throw error;
  }
};

const normalizePaginatedResponse = (responseData, fallback = {}) => {
  const pagination = responseData?.pagination || {};

  return {
    ...responseData,
    total: responseData?.total ?? pagination.total ?? fallback.total ?? 0,
    page: responseData?.page ?? responseData?.currentPage ?? pagination.page ?? fallback.page ?? 1,
    currentPage: responseData?.currentPage ?? responseData?.page ?? pagination.page ?? fallback.page ?? 1,
    limit: responseData?.limit ?? pagination.limit ?? fallback.limit ?? 10,
    totalPages:
      responseData?.totalPages ??
      responseData?.pages ??
      pagination.totalPages ??
      pagination.pages ??
      fallback.totalPages ??
      1,
    from: responseData?.from ?? pagination.from ?? fallback.from ?? 0,
    to: responseData?.to ?? pagination.to ?? fallback.to ?? 0
  };
};

const getPdfResponse = (url) => api.get(url, { responseType: 'blob' });

// Inventory API calls
const _inventoryAPI = {
  getInventory: (params = {}) => {
    return safeApiCall(
      api.get('/inventory', { params }),
      { 
        data: [], 
        total: 0, 
        page: params.page || 1, 
        limit: params.limit || 10, 
        totalPages: 0 
      }
    );
  },
  
  getInventoryItem: (id) => safeApiCall(
    api.get(`/inventory/${id}`), 
    null
  ),
  
  searchInventory: (query) => safeApiCall(
    api.get('/inventory/search', { params: { query } }),
    { data: [] }
  ),

  createInventoryItem: (data) => api.post('/inventory', data),

  updateInventory: async (id, data) => {
    const response = await api.put(`/inventory/${id}`, data);
    return response.data;
  }
};

// Requests API calls
const _requestsAPI = {
  getRequests: async (params = {}) => {
    const fallback = {
      data: [],
      total: 0,
      page: params.page || 1,
      limit: params.limit || 10,
      totalPages: 1
    };

    const data = await safeApiCall(
      api.get('/requests', { params }),
      fallback
    );

    return normalizePaginatedResponse(data, fallback);
  },
  
  getCustomerRequests: async (params = {}) => {
    const fallback = {
      data: [],
      total: 0,
      page: params.page || 1,
      limit: params.limit || 10,
      totalPages: 1
    };

    const data = await safeApiCall(
      api.get('/requests', { params }),
      fallback
    );

    return normalizePaginatedResponse(data, fallback);
  },
  
  getRequest: (id) => safeApiCall(
    api.get(`/requests/${id}`),
    null
  ),

  getManifestEdit: (id) => safeApiCall(
    api.get(`/requests/${id}/manifest-edit`),
    null
  ),

  updateManifestEdit: (id, data) => api.patch(`/requests/${id}/manifest-edit`, data).then(r => r.data),

  // Fetch the browser-printable shipment label sheet as HTML text. The
  // backend route is auth-gated, so this has to go through axios (with
  // the bearer token) rather than a plain window.open. Caller is
  // expected to open a new window and write the returned HTML into it.
  getShipmentLabels: (id) => api.get(`/requests/${id}/shipment-labels`, { responseType: 'text' }).then(r => r.data),

  createRequest: (data) => api.post('/requests', data).then(r => r.data),
  
  updateRequest: (id, data) => api.put(`/requests/${id}`, data),
  
  cancelRequest: (id) => api.post(`/requests/${id}/cancel`)
};

// Shipping API calls
const _shippingAPI = {
  // Shipment management
  getShipments: (params = {}) => {
    return safeApiCall(
      api.get('/shipping/shipments', { params }),
      { 
        data: [], 
        total: 0, 
        page: params.page || 1, 
        limit: params.limit || 10, 
        totalPages: 0 
      }
    );
  },
  
  getShipment: (id) => safeApiCall(
    api.get(`/shipping/shipments/${id}`),
    null
  ),
  
  createShipment: (requestId, data) => api.post(`/shipping/requests/${requestId}/shipments`, data),

  getRequestPackages: (requestId) => api.get(`/shipping/requests/${requestId}/packages`).then(r => r.data),

  createRequestPackage: (requestId, data) => api.post(`/shipping/requests/${requestId}/packages`, data),

  createShipmentFromPackages: (requestId, packageIds = []) =>
    api.post(`/shipping/requests/${requestId}/packages/shipments`, { packageIds }),

  updateRequestPackage: (requestId, packageId, data) =>
    api.patch(`/shipping/requests/${requestId}/packages/${packageId}`, data),

  updateRequestPackageLine: (requestId, packageId, lineItemId, data) =>
    api.patch(`/shipping/requests/${requestId}/packages/${packageId}/lines/${lineItemId}`, data),

  cancelRequestPackage: (requestId, packageId, reason = null) =>
    api.delete(`/shipping/requests/${requestId}/packages/${packageId}`, { data: { reason } }),
  
  updateShipmentStatus: (id, data) => api.patch(`/shipping/shipments/${id}/status`, data),

  completePickupShipment: (id, data) => api.post(`/shipping/shipments/${id}/complete-pickup`, data),

  completeCarrierShipment: (id, data) => api.post(`/shipping/shipments/${id}/complete-carrier`, data),
  
  cancelShipment: (id) => api.delete(`/shipping/shipments/${id}`),

  updateShipmentPackages: (shipmentId, packages) =>
    api.put(`/shipping/shipments/${shipmentId}/packages`, { packages }),

  getPackageRates: (shipmentId, packageId, shippingMethod) => {
    const params = shippingMethod ? { shipping_method: shippingMethod } : {};
    return safeApiCall(
      api.get(`/shipping/shipments/${shipmentId}/packages/${packageId}/rates`, { params }),
      { rates: [], recommendation: null }
    );
  },

  purchasePackageLabel: (shipmentId, packageId, rateId, labelFormat = 'PDF', easypostShipmentId = null) =>
    api.post(`/shipping/shipments/${shipmentId}/packages/${packageId}/purchase-label`, {
      rate_id: rateId,
      label_format: labelFormat,
      easypost_shipment_id: easypostShipmentId
    }),
  
  // Shipping rates
  getShippingRates: (requestId, shippingMethod) => {
    const params = shippingMethod ? { shipping_method: shippingMethod } : {};
    return safeApiCall(
      api.get(`/shipping/requests/${requestId}/rates`, { params }),
      { rates: [], recommendation: null }
    );
  },
  
  // Demo shipping rate calculations from items array
  calculateDemoRatesFromItems: (items, shippingMethod = 'courier') => {
    // Calculate total weight and volume from items
    const totalWeight = items.reduce((sum, item) => {
      const itemWeight = parseFloat(item.weight || 5); // Default 5 lbs per item
      return sum + (itemWeight * parseFloat(item.quantity || 0));
    }, 0);
    
    const totalVolume = items.reduce((sum, item) => {
      const itemVolume = parseFloat(item.volume || 1); // Default 1 cu ft per item
      return sum + (itemVolume * parseFloat(item.quantity || 0));
    }, 0);
    
    return _shippingAPI.calculateDemoRates(totalWeight, totalVolume, shippingMethod);
  },

  // Demo rates API endpoint (frontend-only simulation)
  getDemoRates: async (requestData) => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const { method, items } = requestData;
    return _shippingAPI.calculateDemoRatesFromItems(items, method);
  },

  // Demo shipping rate calculations (frontend-only)
  calculateDemoRates: (weight, volume, shippingMethod = 'courier') => {
    if (shippingMethod === 'pickup') {
      return [
        {
          carrier: 'Customer Pickup',
          service: 'Pickup',
          cost: 0,
          transit_days: 'N/A'
        }
      ];
    }

    if (shippingMethod === 'courier') {
      return [
        {
          carrier: 'FedEx',
          service: 'Ground',
          cost: Math.max(15, weight * 0.85 + volume * 2.5),
          transit_days: '1-5'
        },
        {
          carrier: 'FedEx',
          service: '2Day',
          cost: Math.max(25, weight * 1.25 + volume * 3.5),
          transit_days: '2'
        },
        {
          carrier: 'FedEx',
          service: 'Overnight',
          cost: Math.max(45, weight * 2.85 + volume * 5.5),
          transit_days: '1'
        },
        {
          carrier: 'UPS',
          service: 'Ground',
          cost: Math.max(14, weight * 0.80 + volume * 2.3),
          transit_days: '1-5'
        },
        {
          carrier: 'UPS',
          service: '2Day Air',
          cost: Math.max(28, weight * 1.35 + volume * 3.8),
          transit_days: '2'
        },
        {
          carrier: 'UPS',
          service: 'Next Day Air',
          cost: Math.max(48, weight * 2.95 + volume * 5.8),
          transit_days: '1'
        }
      ].sort((a, b) => a.cost - b.cost);
    } else {
      // Freight calculations
      const baseCost = 50;
      const perCuFtRate = 0.75;
      const weightSurcharge = weight > 500 ? (weight - 500) * 0.15 : 0;
      
      return [
        {
          carrier: 'ABF Freight',
          service: 'Standard',
          cost: baseCost + (volume * perCuFtRate) + weightSurcharge,
          transit_days: '3-7'
        },
        {
          carrier: 'Western Canada Express',
          service: 'Standard',
          cost: baseCost + (volume * (perCuFtRate + 0.05)) + weightSurcharge,
          transit_days: '3-7'
        },
        {
          carrier: 'Day & Ross',
          service: 'Standard',
          cost: baseCost + (volume * (perCuFtRate + 0.10)) + weightSurcharge,
          transit_days: '2-5'
        },
        {
          carrier: 'FedEx Freight',
          service: 'Standard',
          cost: baseCost + (volume * (perCuFtRate + 0.15)) + weightSurcharge,
          transit_days: '2-5'
        }
      ].sort((a, b) => a.cost - b.cost);
    }
  },
  
  // Available inventory for shipping
  getAvailableInventory: (params = {}) => {
    return safeApiCall(
      api.get('/shipping/inventory/available', { params }),
      { data: [] }
    );
  },
  
  // Customer addresses
  getCustomerAddresses: (companyId) => safeApiCall(
    api.get(`/shipping/addresses/companies/${companyId}`),
    { data: [] }
  ),
  
  getAddress: (addressId) => safeApiCall(
    api.get(`/shipping/addresses/${addressId}`),
    null
  ),
  
  validateAddress: (address) => api.post('/shipping/addresses/validate', address),

  // EasyPost integration
  getEasyPostStatus: () => safeApiCall(
    api.get('/shipping/easypost/status'),
    { enabled: false, carriers: [] }
  ),

  purchaseLabel: (shipmentId, rateId, labelFormat = 'PDF', easypostShipmentId = null) =>
    api.post(`/shipping/shipments/${shipmentId}/purchase-label`, {
      rate_id: rateId,
      label_format: labelFormat,
      easypost_shipment_id: easypostShipmentId
    }),

  refundLabel: (shipmentId) =>
    api.post(`/shipping/shipments/${shipmentId}/refund-label`),

  getTrackingInfo: (shipmentId) => safeApiCall(
    api.get(`/shipping/shipments/${shipmentId}/tracking`),
    { tracking: null }
  ),

  validateAddressEasyPost: (address) =>
    api.post('/shipping/addresses/validate-easypost', address),

  getPackingSlipUrl: (shipmentId) =>
    `${api.defaults.baseURL}/shipping/shipments/${shipmentId}/packing-slip`,

  // Inventory breakdown for shipping form
  getInventoryBreakdown: (productId, params = {}) => safeApiCall(
    api.get('/shipping/inventory/breakdown', { params: { product_id: productId, ...params } }),
    { total: 0, pallets: 0, pallet_units: 0, individual: 0, projects: [] }
  ),

  // Default warehouse resolution
  getDefaultWarehouse: (companyId) => safeApiCall(
    api.get('/shipping/warehouse/default', { params: { company_id: companyId } }),
    { warehouse: null, source: 'none', warehouses: [] }
  )
};

// Invoices API calls
const _invoicesAPI = {
  getInvoices: async (params = {}) => {
    const { page = 1, limit = 10, search = '', status = '', dateRange = 'all' } = params;
    const queryParams = new URLSearchParams({
      page,
      limit,
      ...(search && { search }),
      ...(status && { status }),
      ...(dateRange !== 'all' && { dateRange })
    });
    const fallback = { data: [], total: 0, page, limit, totalPages: 1 };
    const data = await safeApiCall(
      api.get(`/billing/invoices?${queryParams}`),
      fallback
    );
    return normalizePaginatedResponse(data, fallback);
  },
  getInvoice: (id) => safeApiCall(
    api.get(`/billing/invoices/${id}`),
    null
  ),
  downloadInvoicePdf: (id) => getPdfResponse(`/billing/invoices/${id}/pdf`),
  createInvoice: (data) => api.post('/invoices', data),

  // Live storage fee snapshot for the current month (KPIs +
  // per-pallet detail) and recent history for the trend widget.
  getStorageSnapshot: () => safeApiCall(
    api.get('/billing/storage/current'),
    { success: false, totals: {}, byWarehouse: [], rateSchedule: [], units: [] }
  ),
  getStorageHistory: (months = 6) => safeApiCall(
    api.get('/billing/storage/history', { params: { months } }),
    { success: false, history: [] }
  )
};

// Admin API calls
const _adminAPI = {
  // Customer management
  getCustomers: (params = {}) => {
    return safeApiCall(
      api.get('/admin/customers', { params }),
      { data: [], total: 0, page: params.page || 1, limit: params.limit || 10, totalPages: 0 }
    );
  },

  searchCustomers: (query) => safeApiCall(
    api.get('/admin/customers/search', { params: { q: query } }),
    { data: [] }
  ),
  
  getCustomer: (id) => safeApiCall(
    api.get(`/admin/customers/${id}`),
    null
  ),
  
  createCustomer: (data) => {
    return api.post('/admin/customers', data);
  },
  
  updateCustomer: (id, data) => api.put(`/admin/customers/${id}`, data),
  
  deleteCustomer: (id) => api.delete(`/admin/customers/${id}`),
  
  deactivateCustomer: (id) => api.post(`/admin/customers/${id}/deactivate`),
  
  // Requests management
  getRequests: async (params = {}) => {
    const fallback = {
      data: [],
      total: 0,
      page: params.page || 1,
      limit: params.limit || 10,
      totalPages: 1
    };

    const data = await safeApiCall(
      api.get('/admin/requests', { params }),
      fallback
    );

    return normalizePaginatedResponse(data, fallback);
  },
  
  getRequest: (id) => safeApiCall(
    api.get(`/admin/requests/${id}`),
    null
  ),

  getManifestEdit: (id) => safeApiCall(
    api.get(`/admin/requests/${id}/manifest-edit`),
    null
  ),

  updateManifestEdit: (id, data) => api.patch(`/admin/requests/${id}/manifest-edit`, data).then(r => r.data),

  getRequestComments: (id) => safeApiCall(
    api.get(`/admin/requests/${id}/comments`),
    { comments: [] }
  ),

  addRequestComment: (id, data) => api.post(`/admin/requests/${id}/comments`, data).then(r => r.data),

  // Inventory corrections (receipt void). The receiving-sessions list is
  // the void picker for ASN receipts; manifest receipts come from
  // manifestReceivingAPI.getManifestReceivingHistory.
  getRequestReceivingSessions: (id) => safeApiCall(
    api.get(`/admin/requests/${id}/receiving-sessions`),
    { data: [] }
  ),

  voidReceipt: (data) => api.post('/admin/corrections/receipt-void', data).then(r => r.data),

  createAdjustment: (data) => api.post('/admin/corrections/adjustments', data).then(r => r.data),

  createTransfer: (data) => api.post('/admin/corrections/transfers', data).then(r => r.data),

  // Stock aggregate vs actual asset count per item -- drift visibility for
  // the adjustment screen.
  getAdjustmentStockStatus: (params) => safeApiCall(
    api.get('/admin/corrections/stock-status', { params }),
    { data: [] }
  ),

  getCorrections: async (params = {}) => {
    const fallback = { data: [], total: 0, page: params.page || 1, totalPages: 1 };
    return safeApiCall(api.get('/admin/corrections', { params }), fallback);
  },

  getCorrection: (id) => safeApiCall(
    api.get(`/admin/corrections/${id}`),
    null
  ),

  // Storage units (RF2): the physical class derives from measured
  // dimensions; billing treatment can diverge only through the
  // role-gated billing-class override.
  getStorageUnits: (params = {}) => safeApiCall(
    api.get('/admin/storage-units', { params }),
    { data: [], pagination: { page: 1, pages: 1, total: 0 } }
  ),

  updateStorageUnit: (id, data) => api.put(`/admin/storage-units/${id}`, data).then(r => r.data),

  setStorageUnitBillingOverride: (id, data) =>
    api.put(`/admin/storage-units/${id}/billing-class-override`, data).then(r => r.data),

  setStorageUnitPhysicalClass: (id, data) =>
    api.put(`/admin/storage-units/${id}/physical-class`, data).then(r => r.data),

  // Billing exceptions (RF14): the ops queue for storage capture
  // failures and missing customer rates.
  getBillingExceptions: async (params = {}) => {
    const fallback = { exceptions: [], total: 0, page: params.page || 1 };
    return safeApiCall(api.get('/admin/billing-exceptions', { params }), fallback);
  },

  getBillingException: (id) => safeApiCall(
    api.get(`/admin/billing-exceptions/${id}`),
    null
  ),

  updateBillingException: (id, data) =>
    api.put(`/admin/billing-exceptions/${id}`, data).then(r => r.data),

  // Printable shipment label sheet (HTML text). See requestsAPI
  // counterpart for why this goes through axios rather than window.open.
  getShipmentLabels: (id) => api.get(`/admin/requests/${id}/shipment-labels`, { responseType: 'text' }).then(r => r.data),

  // Printable pallet label sheet (HTML text). Accepts optional palletIds
  // array and layout ('full' or 'half'). Returns self-contained HTML.
  getPalletLabels: async (requestId, { palletIds, layout } = {}) => {
    const params = new URLSearchParams();
    if (palletIds && palletIds.length > 0) params.append('palletIds', palletIds.join(','));
    if (layout) params.append('layout', layout);
    const response = await api.get(`/admin/requests/${requestId}/pallet-labels?${params.toString()}`, { responseType: 'text' });
    return response.data;
  },

  createRequest: (data) => api.post('/admin/requests', data).then(r => r.data),
  
  updateRequest: (id, data) => api.put(`/admin/requests/${id}`, data).then(r => r.data),
  
  updateRequestStatus: (id, data) => api.put(`/admin/requests/${id}/status`, data),

  cancelRequest: (id, data) => api.post(`/admin/requests/${id}/cancel`, data),
  
  checkCanDeleteRequest: (id) => safeApiCall(
    api.get(`/admin/requests/${id}/can-delete`),
    { canDelete: false, reason: 'Permission denied' }
  ),
  
  deleteRequest: (id) => api.delete(`/admin/requests/${id}`),
  
  // Inventory asset management (project assignments)
  getInventoryAssets: (itemId, params = {}) => safeApiCall(
    api.get(`/admin/inventory/${itemId}/assets`, { params }),
    { data: [], total: 0 }
  ),
  updateAssetProject: (data) => api.put('/admin/inventory/assets/project', data).then(r => r.data),
  updateAssetIdentifiers: (data) => api.put('/admin/inventory/assets/identifiers', data).then(r => r.data),

  // Cross-dock pallet label generation
  generateCrossDockPalletLabels: (data) => api.post('/admin/crossdock/generate-pallet-labels', data).then(r => r.data),

  // Cross-dock workflow
  receiveCrossDockPallet: (palletId, data) => api.post(`/crossdock/pallets/${palletId}/receive`, data).then(r => r.data),
  receiveAllCrossDockPallets: (requestId, data) => api.post(`/crossdock/requests/${requestId}/receive-all`, data).then(r => r.data),
  bookCrossDockShipment: (data) => api.post('/crossdock/pallets/book-shipment', data).then(r => r.data),
  markCrossDockShipped: (data) => api.post('/crossdock/pallets/mark-shipped', data).then(r => r.data),
  provideCrossDockDocs: (data) => api.post('/crossdock/pallets/provide-docs', data).then(r => r.data),
  getCrossDockSettings: () => safeApiCall(api.get('/crossdock/settings'), { max_crossdock_business_days: null }),
  updateCrossDockSettings: (data) => api.put('/crossdock/settings', data).then(r => r.data),
  
  // Invoice management
  getInvoices: (params = {}) => {
    const { page = 1, limit = 10, search = '', status = '', customerId = '', dateRange = 'all' } = params;
    
    const queryParams = new URLSearchParams({
      page,
      limit,
      ...(search && { search }),
      ...(status && { status }),
      ...(customerId && { customerId }),
      ...(dateRange !== 'all' && { dateRange })
    });
    
    return safeApiCall(
      api.get(`/admin/invoices?${queryParams}`),
      { 
        data: [], 
        total: 0, 
        page: page, 
        limit: limit, 
        totalPages: 0,
        from: 0,
        to: 0
      }
    );
  },
  getInvoice: (id) => safeApiCall(
    api.get(`/admin/invoices/${id}`),
    null
  ),
  createInvoice: (data) => api.post('/admin/invoices', data),
  // RF9: labour rate resolved server-side for the request's warehouse.
  getManualBillingRates: (requestId) => api.get(`/admin/requests/${requestId}/manual-billing-rates`).then(r => r.data),
  // The admin route is a PUT -- this was a POST for its entire life, so
  // admin status updates never worked from the UI (RF11).
  updateInvoiceStatus: (id, data) => api.put(`/admin/invoices/${id}/status`, data),
  
  // User management
  getUsers: async (params = {}) => {
    const fallback = {
      data: [],
      total: 0,
      page: params.page || 1,
      limit: params.limit || 10,
      totalPages: 1
    };

    const data = await safeApiCall(
      api.get('/admin/users', { params }),
      fallback
    );

    return normalizePaginatedResponse(data, fallback);
  },
  
  getRoles: () => safeApiCall(
    api.get('/admin/roles'),
    { data: [] }
  ),
  
  getUsersByCustomer: async (customerId) => {
    try {
      const response = await api.get(`/admin/customers/${customerId}/users`);
      
      // If the response already has a data property with an array, return it
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        return response.data;
      }
      
      // If the response data is an array directly, wrap it in a data object
      if (response.data && Array.isArray(response.data)) {
        return { data: response.data };
      }
      
      // If we just have general response data, check if it's an array
      if (Array.isArray(response.data)) {
        return { data: response.data };
      }
      
      // Fallback to empty array with warning
      return { data: [] };
    } catch (error) {
      console.error('Error fetching users for customer:', error);
      // Return empty data structure on error
      return { data: [] };
    }
  },

  createCustomerUser: async (customerId, userData) => {
    try {
      const response = await api.post(`/admin/customers/${customerId}/users`, userData);
      return response.data;
    } catch (error) {
      console.error('Error creating customer user:', error.response?.data || error);
      throw error;
    }
  },

  updateCustomerUser: async (customerId, userId, userData) => {
    try {
      const response = await api.put(`/admin/customers/${customerId}/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      console.error('Error updating customer user:', error.response?.data || error);
      throw error;
    }
  },

  activateCustomerUser: async (customerId, userId) => {
    try {
      const response = await api.post(`/admin/customers/${customerId}/users/${userId}/activate`);
      return response.data;
    } catch (error) {
      console.error('Error activating customer user:', error.response?.data || error);
      throw error;
    }
  },

  deactivateCustomerUser: async (customerId, userId) => {
    try {
      const response = await api.post(`/admin/customers/${customerId}/users/${userId}/deactivate`);
      return response.data;
    } catch (error) {
      console.error('Error deactivating customer user:', error.response?.data || error);
      throw error;
    }
  },
  
  getUser: (id) => safeApiCall(
    api.get(`/admin/users/${id}`),
    null
  ),
  
  createUser: async (userData) => {
    try {
      const response = await api.post('/admin/users', userData);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error.response?.data || error);
      throw error;
    }
  },
  
  updateUser: async (userId, userData) => {
    try {
      const response = await api.put(`/admin/users/${userId}`, userData);
      
      // Ensure we return a consistent data structure
      if (response.data && response.data.data) {
        // Server returned the data in a nested 'data' property
        return response.data;
      } else if (response.data) {
        // Server returned data directly, wrap it for consistency
        return {
          message: response.data.message || 'User updated successfully',
          data: response.data
        };
      } else {
        // Fallback for unexpected response format
        return {
          message: 'User updated but response format was unexpected',
          data: { id: userId, ...userData }
        };
      }
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },
  
  deactivateUser: async (userId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/deactivate`);
      return response.data;
    } catch (error) {
      console.error('Error deactivating user:', error);
      throw error;
    }
  },
  
  activateUser: async (userId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/activate`);
      return response.data;
    } catch (error) {
      console.error('Error activating user:', error);
      throw error;
    }
  },

  resendWelcomeEmail: async (userId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/resend-welcome`);
      return response.data;
    } catch (error) {
      console.error('Error resending welcome email:', error);
      throw error;
    }
  },

  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },
  
  // Inventory management
  getInventory: (params = {}) => {
    return safeApiCall(
      api.get('/admin/inventory', { params }),
      { 
        data: [], 
        total: 0, 
        page: params.page || 1, 
        limit: params.limit || 10, 
        totalPages: 0 
      }
    );
  },
  
  // Dashboard stats
  getDashboardStats: () => safeApiCall(
    api.get('/admin/dashboard/stats'),
    {
      customerCount: 0,
      inventoryCount: 0,
      pendingRequestsCount: 0,
      unpaidInvoicesCount: 0,
      recentActivity: []
    }
  ),

  getHelpDocs: () => safeApiCall(
    api.get('/admin/help-docs'),
    { docs: [] }
  ),

  // Permission management
  getAllPermissions: () => safeApiCall(
    api.get('/admin/permissions'),
    { data: [] }
  ),

  getDefaultPermissions: () => safeApiCall(
    api.get('/admin/permissions/default'),
    { data: {} }
  ),

  getUserPermissions: (userId) => safeApiCall(
    api.get(`/admin/users/${userId}/permissions`),
    { data: {} }
  ),

  updateUserPermissions: async (userId, permissions, role = 'regular_user') => {
    try {
      const response = await api.put(`/admin/users/${userId}/permissions`, {
        permissions,
        role
      });
      return response.data;
    } catch (error) {
      console.error('Error updating user permissions:', error);
      throw error;
    }
  },

  checkUserPermission: (userId, permission) => safeApiCall(
    api.get(`/admin/users/${userId}/permissions/${permission}`),
    { hasPermission: false }
  ),

  // System Settings management
  getSystemSettings: () => safeApiCall(
    api.get('/system-settings'),
    {
      company_name: '',
      billing_address_line1: '',
      billing_address_line2: '',
      billing_city: '',
      billing_state_province: '',
      billing_postal_code: '',
      billing_country: '',
      billing_email: '',
      billing_phone: '',
      default_timezone: 'America/Edmonton',
      default_date_format: 'MM/DD/YYYY',
      default_time_format: '24h',
      default_internal_wh: null,
      default_customer_wh: null,
      contact_name: '',
      contact_email: '',
      support_email: '',
      support_notification_emails: [],
      notification_preferences: {
        request_created: true,
        invoice_created: true,
        manifest_updates: true,
        crossdock_updates: true,
        low_stock: true
      },
      contact_phone: '',
      logo_url: '',
      isFirstTime: true
    }
  ),

  // System Branding (public for all authenticated users)
  getSystemBranding: () => safeApiCall(
    api.get('/system-settings/branding'),
    {
      company_name: '3PL System',
      logo_url: null
    }
  ),

  getAppVersion: () => safeApiCall(
    api.get('/system-settings/app-version'),
    { version: process.env.REACT_APP_APP_VERSION || 'dev' }
  ),

  getChangelog: (params = {}) => safeApiCall(
    api.get('/system-settings/changelog', { params }),
    {
      version: process.env.REACT_APP_APP_VERSION || 'dev',
      scope: params.scope || 'customer',
      recentWindowDays: 90,
      fallbackToLatest: false,
      entries: []
    }
  ),

  getChangelogEntriesForSettings: () => safeApiCall(
    api.get('/system-settings/changelog/manage'),
    { entries: [] }
  ),

  createChangelogEntry: async (data) => {
    const response = await api.post('/system-settings/changelog/manage', data);
    return response.data;
  },

  updateChangelogEntry: async (id, data) => {
    const response = await api.put(`/system-settings/changelog/manage/${id}`, data);
    return response.data;
  },

  deleteChangelogEntry: async (id) => {
    const response = await api.delete(`/system-settings/changelog/manage/${id}`);
    return response.data;
  },

  updateSystemSettings: async (settingsData) => {
    try {
      let requestData;
      let config = {};
      
      // Check if there's a logo file to upload
      if (settingsData.logo_file && settingsData.logo_file instanceof File) {
        // Use FormData for file upload
        requestData = new FormData();
        
        // Append all form fields
        Object.keys(settingsData).forEach(key => {
          if (key === 'logo_file') {
            requestData.append('logo_file', settingsData.logo_file);
          } else if (settingsData[key] && typeof settingsData[key] === 'object') {
            requestData.append(key, JSON.stringify(settingsData[key]));
          } else {
            requestData.append(key, settingsData[key]);
          }
        });
        
        // Don't set Content-Type for FormData - let axios handle it
        // This allows multipart boundary to be set automatically
      } else {
        // Use regular JSON for non-file updates
        requestData = settingsData;
        config.headers = {
          'Content-Type': 'application/json'
        };
      }
      
      const response = await api.put('/system-settings', requestData, config);
      return response.data;
    } catch (error) {
      console.error('Error updating system settings:', error);
      throw error;
    }
  },

  getTimezones: () => safeApiCall(
    api.get('/system-settings/timezones'),
    [
      { value: 'America/New_York', label: 'Eastern Time (ET)' },
      { value: 'America/Chicago', label: 'Central Time (CT)' },
      { value: 'America/Denver', label: 'Mountain Time (MT)' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
      { value: 'America/Edmonton', label: 'Mountain Time - Edmonton (MT)' },
      { value: 'UTC', label: 'Coordinated Universal Time (UTC)' }
    ]
  ),

  // Role management
  getRolePermissions: async (roleId) => {
    try {
      const response = await api.get(`/admin/roles/${roleId}/permissions`);
      return response.data;
    } catch (error) {
      console.error('Error getting role permissions:', error);
      throw error;
    }
  },

  updateRolePermissions: async (roleId, permissions) => {
    try {
      const response = await api.put(`/admin/roles/${roleId}/permissions`, { permissions });
      return response.data;
    } catch (error) {
      console.error('Error updating role permissions:', error);
      throw error;
    }
  },

  // Warehouse management
  getWarehouses: (params = {}) => safeApiCall(
    api.get('/warehouses', { params }),
    []
  ),

  getWarehouse: (id) => safeApiCall(
    api.get(`/warehouses/${id}`),
    null
  ),

  createWarehouse: async (warehouseData) => {
    try {
      const response = await api.post('/warehouses', warehouseData);
      return response.data;
    } catch (error) {
      console.error('Error creating warehouse:', error);
      throw error;
    }
  },

  updateWarehouse: async (id, warehouseData) => {
    try {
      const response = await api.put(`/warehouses/${id}`, warehouseData);
      return response.data;
    } catch (error) {
      console.error('Error updating warehouse:', error);
      throw error;
    }
  },

  deleteWarehouse: async (id) => {
    try {
      const response = await api.delete(`/warehouses/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting warehouse:', error);
      throw error;
    }
  },

  // Landing page branding management
  getLandingBranding: () => safeApiCall(
    api.get('/system-settings/landing-branding'),
    { site_title: '', has_landing_logo: false, has_favicon: false }
  ),

  updateLandingBranding: async (formData) => {
    // Do not set Content-Type manually -- let axios set the multipart boundary automatically
    const response = await api.put('/system-settings/landing-branding', formData);
    return response.data;
  },

  // Admin Warehouse management
  getAdminWarehouses: async () => {
    const response = await api.get('/admin/warehouses');
    return response.data;
  },

  // Audit Logs
  getAuditLogs: (params = {}) => {
    return safeApiCall(
      api.get('/admin/audit-logs', { params }),
      { data: [], total: 0, page: 1, limit: 25, totalPages: 0 }
    );
  },

  getEntityTimeline: (entityType, entityId, params = {}) => {
    return safeApiCall(
      api.get(`/admin/audit-logs/${entityType}/${entityId}`, { params }),
      { data: [], total: 0, page: 1, limit: 50, totalPages: 0 }
    );
  },

  // Contracts
  getContracts: (params = {}) => safeApiCall(
    api.get('/admin/contracts', { params }),
    { data: [], total: 0, page: 1, totalPages: 0 }
  ),
  getContract: (id) => safeApiCall(api.get(`/admin/contracts/${id}`), null),
  createContract: (formData) => api.post('/admin/contracts', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateContract: (id, formData) => api.put(`/admin/contracts/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteContract: (id) => api.delete(`/admin/contracts/${id}`),

  // Zoho Books integration
  getZohoStatus: () => safeApiCall(api.get('/admin/zoho/status'), { configured: false }),
  downloadInvoicePdf: (invoiceId) => getPdfResponse(`/admin/invoices/${invoiceId}/pdf`),
  syncInvoiceToZoho: (invoiceId) => api.post(`/admin/invoices/${invoiceId}/zoho-sync`),
  sendInvoiceViaZoho: (invoiceId, options = {}) => api.post(`/admin/invoices/${invoiceId}/zoho-send`, options),
  getPaymentLink: (invoiceId) => safeApiCall(api.get(`/admin/invoices/${invoiceId}/payment-link`), { success: false }),
  pullInvoiceStatus: (invoiceId) => api.post(`/admin/invoices/${invoiceId}/zoho-pull`),
  syncCustomerToZoho: (companyId) => api.post(`/admin/customers/${companyId}/zoho-sync`),
  getZohoWebhookEvents: (params = {}) => safeApiCall(
    api.get('/admin/zoho/webhook-events', { params }),
    { data: [], total: 0, page: 1, limit: 25, totalPages: 0, summary: { pending: 0, processed: 0, dead_letter: 0, total: 0 } }
  ),
  getZohoWebhookEvent: (id) => safeApiCall(api.get(`/admin/zoho/webhook-events/${id}`), null),
  replayZohoWebhookEvent: (id) => api.post(`/admin/zoho/webhook-events/${id}/replay`),
};

// Inbound Projects API calls
const _inboundProjectsAPI = {
  // Get all projects for customer/admin
  getProjects: (params = {}) => {
    return safeApiCall(
      api.get('/inbound-projects', { params }),
      { 
        projects: [], 
        pagination: {
          page: params.page || 1, 
          limit: params.limit || 10, 
          total: 0,
          pages: 0
        }
      }
    );
  },
  
  // Get single project
  getProject: (id) => safeApiCall(
    api.get(`/inbound-projects/${id}`),
    null
  ),
  
  // Create new project
  createProject: (data) => api.post('/inbound-projects', data),
  
  // Update project
  updateProject: (id, data) => api.put(`/inbound-projects/${id}`, data),
  
  // Delete project
  deleteProject: (id) => api.delete(`/inbound-projects/${id}`),
  
  // Upload CSV file
  uploadProjectFile: (formData) => {
    return api.post('/inbound-projects/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Upload file for specific project
  uploadProjectFileForProject: (id, formData) => {
    return api.post(`/inbound-projects/${id}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Get project lines
  getProjectLines: (id) => safeApiCall(
    api.get(`/inbound-projects/${id}/lines`),
    { lines: [] }
  ),
  
  // Create project line
  createProjectLine: (id, data) => api.post(`/inbound-projects/${id}/lines`, data),
  
  // Update project line
  updateProjectLine: (id, lineId, data) => api.put(`/inbound-projects/${id}/lines/${lineId}`, data),
  
  // Delete project line
  deleteProjectLine: (id, lineId) => api.delete(`/inbound-projects/${id}/lines/${lineId}`),
  
  // Match line to inventory item
  matchLineToInventoryItem: (id, lineId, data) => api.post(`/inbound-projects/${id}/lines/${lineId}/match`, data),
  
  // Create placeholder item for line
  createPlaceholderItem: (id, lineId) => api.post(`/inbound-projects/${id}/lines/${lineId}/create-placeholder`),
  
  // Get SKU suggestions for matching
  getSkuSuggestions: (id, search) => safeApiCall(
    api.get(`/inbound-projects/${id}/sku-suggestions`, { params: { search } }),
    { suggestions: [] }
  ),
  
  // Project status management
  submitProject: (id) => api.post(`/inbound-projects/${id}/submit`),
  validateProject: (id) => api.post(`/inbound-projects/${id}/validate`),
  cancelProject: (id, data) => api.post(`/inbound-projects/${id}/cancel`, data),
  
  // Validation
  validateProjectData: (id) => safeApiCall(
    api.get(`/inbound-projects/${id}/validate`),
    { validation: { isValid: false, errors: [], warnings: [], summary: {} } }
  ),
  
  // Receipts management
  getProjectReceipts: (id) => safeApiCall(
    api.get(`/inbound-projects/${id}/receipts`),
    { receipts: [] }
  ),
  
  createReceipt: (id, data) => api.post(`/inbound-projects/${id}/receipts`, data),
  getReceipt: (id, receiptId) => safeApiCall(
    api.get(`/inbound-projects/${id}/receipts/${receiptId}`),
    null
  ),
  
  updateReceipt: (id, receiptId, data) => api.put(`/inbound-projects/${id}/receipts/${receiptId}`, data),
  completeReceipt: (id, receiptId) => api.post(`/inbound-projects/${id}/receipts/${receiptId}/complete`),
  
  // Exceptions management
  getProjectExceptions: (id, params = {}) => safeApiCall(
    api.get(`/inbound-projects/${id}/exceptions`, { params }),
    { exceptions: [] }
  ),
  
  createException: (id, data) => api.post(`/inbound-projects/${id}/exceptions`, data),
  updateException: (id, exceptionId, data) => api.put(`/inbound-projects/${id}/exceptions/${exceptionId}`, data),
  resolveException: (id, exceptionId, data) => api.post(`/inbound-projects/${id}/exceptions/${exceptionId}/resolve`, data),
  
  // Reporting
  getProjectSummary: (id) => safeApiCall(
    api.get(`/inbound-projects/${id}/summary`),
    { summary: {} }
  ),
  
  getProjectProgress: (id) => safeApiCall(
    api.get(`/inbound-projects/${id}/progress`),
    { progress: {} }
  )
};

// Manifest Receiving API calls
const _manifestReceivingAPI = {
  // Get manifests list for admin
  getManifests: async (params = {}) => {
    try {
      const response = await api.get('/admin/manifests', { params });
      return response.data;
    } catch (error) {
      console.error('Get manifests error:', error);
      throw error;
    }
  },

  // Get manifest statistics
  getManifestStats: async () => {
    try {
      const response = await api.get('/admin/manifests/stats');
      return response.data;
    } catch (error) {
      console.error('Get manifest stats error:', error);
      throw error;
    }
  },

  // Get manifest for receiving
  getManifest: async (manifestId) => {
    try {
      const response = await api.get(`/admin/manifests/${manifestId}`);
      return response.data;
    } catch (error) {
      console.error('Get manifest error:', error);
      throw error;
    }
  },

  // Approve manifest
  approveManifest: async (manifestId, approvalNotes) => {
    try {
      const response = await api.post(`/admin/manifests/${manifestId}/approve`, {
        approval_notes: approvalNotes
      });
      return response.data;
    } catch (error) {
      console.error('Approve manifest error:', error);
      throw error;
    }
  },

  // Deny manifest
  denyManifest: async (manifestId, denialNotes) => {
    try {
      const response = await api.post(`/admin/manifests/${manifestId}/deny`, {
        denial_notes: denialNotes
      });
      return response.data;
    } catch (error) {
      console.error('Deny manifest error:', error);
      throw error;
    }
  },

  // Process manifest receipt
  processManifestReceipt: async (manifestId, receiptData) => {
    try {
      const response = await api.post(`/admin/manifests/${manifestId}/receipts`, receiptData);
      return response.data;
    } catch (error) {
      console.error('Process manifest receipt error:', error);
      throw error;
    }
  },

  // Get manifest receiving history
  getManifestReceivingHistory: async (manifestId) => {
    try {
      const response = await api.get(`/admin/manifests/${manifestId}/history`);
      return response.data;
    } catch (error) {
      console.error('Get manifest history error:', error);
      throw error;
    }
  },

  // Hold a manifest line
  holdManifestLine: async (manifestId, lineId, { holdReason, holdQuantity }) => {
    const response = await api.post(`/admin/manifests/${manifestId}/lines/${lineId}/hold`, {
      hold_reason: holdReason,
      hold_quantity: holdQuantity
    });
    return response.data;
  },

  // Cancel held items on a manifest line
  cancelHeldManifestLine: async (manifestId, lineId, { cancelReason, cancelQuantity }) => {
    const response = await api.post(`/admin/manifests/${manifestId}/lines/${lineId}/cancel-held`, {
      cancel_reason: cancelReason,
      cancel_quantity: cancelQuantity
    });
    return response.data;
  },

  // Receive items from hold on a manifest line
  receiveFromHoldManifestLine: async (manifestId, lineId, { quantity, binLocation, warehouseId }) => {
    const response = await api.post(`/admin/manifests/${manifestId}/lines/${lineId}/receive-from-hold`, {
      quantity,
      bin_location: binLocation,
      warehouse_id: warehouseId
    });
    return response.data;
  },

  // Receive pallets (pallet-first flow)
  receivePallets: async (manifestId, data) => {
    const response = await api.post(`/admin/manifests/${manifestId}/receive-pallets`, data);
    return response.data;
  },

  // Upload receipt photos
  uploadReceiptPhotos: async (receiptId, receiptLineId, photos) => {
    try {
      const formData = new FormData();
      photos.forEach(photo => {
        formData.append('photos', photo);
      });

      const response = await api.post(`/admin/manifests/receipts/${receiptId}/lines/${receiptLineId}/photos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Upload receipt photos error:', error);
      throw error;
    }
  }
};

// Documents API calls
const _documentsAPI = {
  // Get documents for a request
  getDocuments: async (requestId) => {
    try {
      const response = await api.get(`/documents/request/${requestId}`);
      return response.data;
    } catch (error) {
      console.error('Get documents error:', error);
      throw error;
    }
  },

  // Upload document for a request
  uploadDocument: async (requestId, file, documentType = 'other', description = '') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', documentType);
      formData.append('description', description);

      const response = await api.post(`/documents/request/${requestId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Upload document error:', error);
      throw error;
    }
  },

  // Get signed URL for viewing a document
  getDocumentUrl: async (requestId, documentId) => {
    try {
      const response = await api.get(`/documents/request/${requestId}/${documentId}/url`);
      return response.data;
    } catch (error) {
      console.error('Get document URL error:', error);
      throw error;
    }
  },

  // Delete a document
  deleteDocument: async (requestId, documentId) => {
    try {
      const response = await api.delete(`/documents/request/${requestId}/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Delete document error:', error);
      throw error;
    }
  },
};

// Check if token exists
const isTokenPresent = () => {
  const token = localStorage.getItem('token');
  return !!token;
};

// Create an object with all our API modules
const apiModules = {
  authAPI: _authAPI,
  notificationsAPI: _notificationsAPI,
  customerAPI: _customerAPI, 
  inventoryAPI: _inventoryAPI,
  requestsAPI: _requestsAPI,
  shippingAPI: _shippingAPI,
  invoicesAPI: _invoicesAPI,
  adminAPI: _adminAPI,
  inboundProjectsAPI: _inboundProjectsAPI,
  documentsAPI: _documentsAPI,
  manifestReceivingAPI: _manifestReceivingAPI
};

// Export individual modules
export const { 
  authAPI,
  notificationsAPI,
  customerAPI, 
  inventoryAPI,
  requestsAPI,
  shippingAPI,
  invoicesAPI,
  adminAPI,
  inboundProjectsAPI,
  documentsAPI,
  manifestReceivingAPI
} = apiModules;

// Export default api
export default api; 
